const toBool = (value: string | undefined) => String(value || '').toLowerCase() === 'true';

const env = process.env as Record<string, string | undefined>;
// Postgres/NAS is the single source of truth for business data.
// Firebase stays for auth/users/roles flows only.
const qnapOnlyMode = true;

export const hybridFlags = {
  useQnapRepairs: qnapOnlyMode || toBool(env.REACT_APP_USE_QNAP_REPAIRS || env.VITE_USE_QNAP_REPAIRS),
  useQnapCustomers: qnapOnlyMode || toBool(env.REACT_APP_USE_QNAP_CUSTOMERS || env.VITE_USE_QNAP_CUSTOMERS),
  useQnapDevices: qnapOnlyMode || toBool(env.REACT_APP_USE_QNAP_DEVICES || env.VITE_USE_QNAP_DEVICES),
  useQnapSpareParts: qnapOnlyMode || toBool(env.REACT_APP_USE_QNAP_SPARE_PARTS || env.VITE_USE_QNAP_SPARE_PARTS),
  useQnapStores: qnapOnlyMode || toBool(env.REACT_APP_USE_QNAP_STORES || env.VITE_USE_QNAP_STORES),
  disableFirestoreReads: qnapOnlyMode || toBool(
    env.REACT_APP_QNAP_DISABLE_FIREBASE_READS || env.VITE_QNAP_DISABLE_FIREBASE_READS
  ),
};

const cloudApiBaseUrl = 'https://api.gamestar.co.il';

/**
 * Same-origin API base — רק כשיש פרוקסי מקומי אמין (לא Netlify edge).
 * Netlify / דומיינים על Netlify (כולל *.gamestar.co.il) → null → קריאה ישירה ל-api.gamestar.co.il
 * (הפרוקסי ב-netlify.toml נופל/מחזיר HTML וגורם ל-response.data === undefined).
 */
function resolveBrowserSameOriginApiBase(): string | null {
  try {
    if (typeof window === 'undefined') return null;
    const host = String(window.location.hostname || '').trim().toLowerCase();
    if (!host || host === 'localhost' || host === '127.0.0.1') return null;
    // LAN — לא same-origin; ייפול ל-REACT_APP_QNAP_API_BASE_URL או לענן
    if (/^10\./.test(host) || /^192\.168\./.test(host) || /^172\.(1[6-9]|2\d|3[0-1])\./.test(host)) {
      return null;
    }
    // אחסון ציבורי (Netlify / custom domain על Netlify) — תמיד API ישיר
    if (/\.netlify\.app$/i.test(host) || /\.gamestar\.co\.il$/i.test(host)) {
      return null;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export const hybridApiBaseUrl =
  resolveBrowserSameOriginApiBase() ||
  env.REACT_APP_QNAP_API_BASE_URL ||
  env.VITE_API_BASE_URL ||
  cloudApiBaseUrl;

const isPrivateNetworkApiUrl = (url: string) =>
  /^http:\/\/(localhost|127\.0\.0\.1|10\.|192\.168\.|172\.(1[6-9]|2\d|3[0-1])\.)/i.test(
    String(url || '').trim(),
  );

/**
 * הודעות — תמיד אותו Postgres כמו REACT_APP_QNAP_API_BASE_URL (NAS).
 * לא מפנים לענן: שם מסד נפרד וקבוצות "נעלמות" לנמענים.
 */
export function resolveMessagingApiBaseUrl(): string {
  const sameOrigin = resolveBrowserSameOriginApiBase();
  if (sameOrigin) return sameOrigin;
  const messagesUrl = String(env.REACT_APP_MESSAGES_API_BASE_URL || '').trim().replace(/\/+$/, '');
  if (messagesUrl) return messagesUrl;
  const qnap = String(env.REACT_APP_QNAP_API_BASE_URL || env.VITE_API_BASE_URL || '').trim().replace(
    /\/+$/,
    '',
  );
  if (qnap) return qnap;
  return cloudApiBaseUrl;
}

/**
 * Socket.IO — ישירות ל-API בענן (Netlify proxy לא תומך WebSocket).
 */
export function resolveHybridSocketBaseUrl(): string | null {
  const explicit = String(env.REACT_APP_ENABLE_REALTIME_SOCKET || env.VITE_ENABLE_REALTIME_SOCKET || '')
    .trim()
    .toLowerCase();
  if (explicit === 'false' || explicit === '0') return null;
  if (resolveBrowserSameOriginApiBase()) {
    return cloudApiBaseUrl;
  }
  return resolveMessagingApiBaseUrl();
}

/** לוג חד-פעמי לקונסולה — מסביר מאיפה אמורים להגיע הנתונים (Firestore מול API). */
export function logHybridDiagnosticsOnce(): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as { __repairLabHybridDiag?: boolean };
  if (w.__repairLabHybridDiag) return;
  w.__repairLabHybridDiag = true;

  const flags = hybridFlags;
  const anyHybrid =
    flags.useQnapRepairs ||
    flags.useQnapCustomers ||
    flags.useQnapDevices ||
    flags.useQnapSpareParts ||
    flags.useQnapStores ||
    flags.disableFirestoreReads;

  const active = Object.entries(flags)
    .filter(([, v]) => v)
    .map(([k]) => k);

  // eslint-disable-next-line no-console
  const socketBaseUrl = resolveHybridSocketBaseUrl();
  console.info('[RepairLab] מקור נתונים:', {
    hybridApiBaseUrl,
    messagesApiBaseUrl: resolveMessagingApiBaseUrl(),
    realtimeSocketUrl: socketBaseUrl || '(כבוי — הודעות ייטענו רק ב-HTTP, איטי יותר)',
    hybridMode: anyHybrid,
    activeHybridFlags: active.length ? active : '(אין — ברירת מחדל Firestore)',
  });

  // בפיתוח בלבד: ערכי env כפי שנכנסו ל-bundle (להשוואה בין מחשבים — אותו .env אמור להניב אותו דבר)
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.info('[RepairLab] ערכי REACT_APP (כפי שנקראו בזמן build של הדפדפן):', {
      REACT_APP_QNAP_API_BASE_URL: env.REACT_APP_QNAP_API_BASE_URL ?? '(לא מוגדר — ישתמש בברירת מחדל)',
      REACT_APP_USE_QNAP_REPAIRS: env.REACT_APP_USE_QNAP_REPAIRS ?? '(לא מוגדר)',
      REACT_APP_USE_QNAP_CUSTOMERS: env.REACT_APP_USE_QNAP_CUSTOMERS ?? '(לא מוגדר)',
      REACT_APP_QNAP_DISABLE_FIREBASE_READS: env.REACT_APP_QNAP_DISABLE_FIREBASE_READS ?? '(לא מוגדר)',
    });
  }

  if (anyHybrid) {
    // eslint-disable-next-line no-console
    console.info(
      '[RepairLab] מצב Postgres-only פעיל: כל הדאטה העסקי (קריאות/לקוחות/מכשירים/חנויות/חלקים) מגיע מה-API של NAS. Firebase משמש רק לאימות/משתמשים/הרשאות.',
    );
  } else {
    // eslint-disable-next-line no-console
    console.info(
      '[RepairLab] מצב ברירת מחדל: תיקונים/לקוחות וכו׳ נטענים מ-Firebase Firestore. נתונים שייבאת רק ל-Postgres (QNAP) לא יופיעו כאן עד שמגדירים ב-.env את REACT_APP_USE_QNAP_REPAIRS וכו׳ ומפעילים מחדש את npm start.',
    );
  }
}
